import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Globe, Award, Zap, Star, ArrowRight, Sparkles, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimatedGradientText } from '../components/ui/animated-gradient-text';
import { WavyBackground } from '../components/ui/WavyBackground';
import { ExploreCategoriesStrip } from '../components/home/ExploreCategoriesStrip';
import { HomeLifecycle } from '../components/home/HomeLifecycle';
import { HomePathsAndPillars } from '../components/home/HomePathsAndPillars';
import { HomeFeaturedDomainsBento } from '../components/home/HomeFeaturedDomainsBento';
import { HomeBusinessBuilder } from '../components/home/HomeBusinessBuilder';
import { HomeServicesSnapshot } from '../components/home/HomeServicesSnapshot';
import { HomeTransformationsAndTestimonials } from '../components/home/HomeTransformationsAndTestimonials';
import { HomeFounderStrip } from '../components/home/HomeFounderStrip';
import { HomeMiniFaq } from '../components/home/HomeMiniFaq';
import { motion, useAnimation, useReducedMotion } from 'framer-motion';
import { CATEGORIES } from '../data/data';
import { supabase } from '@/integrations/supabase/client';
import { Seo, withTemplate, canonicalize, defaultSite } from '@/lib/seo';
import { orgJsonLd } from '@/lib/structuredData';
import JsonLD from './JsonLD';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import {
  gridContainerVariants,
  statsContainerVariants,
  statsItemVariants,
  getGridItemVariants,
} from '@/lib/animations';

// Lightweight, optimized parallax engine scoped to this file
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}

const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CTA_DEMO_CLASSES =
  'inline-flex items-center justify-center gap-2 rounded-2xl font-poppins tracking-wide font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0000FF] min-h-[52px] px-16 py-5 text-xl relative overflow-hidden transition-transform transition-colors duration-300 ease-out select-none';

const shimmerBackground =
  'linear-gradient(180deg, #F3F7FF 0%, #2F62FF 45%, #0037FF 100%)';

const borderGlowGradient =
  'linear-gradient(135deg, rgba(102, 178, 255, 0.95), rgba(160, 118, 255, 0.9), rgba(102, 178, 255, 0.95))';

const MotionLink = motion.create(Link);

function SignatureCtaLink({ to, children }: { to: string; children: React.ReactNode }) {
  const prefersReducedMotionHook = useReducedMotion();
  const shimmerControls = useAnimation();
  const textControls = useAnimation();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (prefersReducedMotionHook) return;
    shimmerControls.set({ opacity: 0, x: '-140%' });
    textControls.set({
      y: 0,
      textShadow: '0px 0px 0px rgba(255,255,255,0)',
      scale: 1,
    });
  }, [prefersReducedMotionHook, shimmerControls, textControls]);

  useEffect(() => {
    if (prefersReducedMotionHook) return;
    if (isHovered) {
      shimmerControls.start({
        opacity: 1,
        x: '140%',
        transition: {
          duration: 1.15,
          ease: 'easeInOut',
          repeat: Infinity,
          repeatType: 'mirror',
        },
      });
      textControls.start({
        y: 0,
        textShadow: '0px 10px 35px rgba(53,121,255,0.55)',
        scale: 1.08,
        transition: { duration: 0.22, ease: 'easeOut' },
      });
    } else {
      shimmerControls.start({
        opacity: 0,
        x: '-140%',
        transition: { duration: 0.28, ease: 'easeOut' },
      });
      textControls.start({
        y: 0,
        textShadow: '0px 0px 0px rgba(0,0,0,0)',
        scale: 1,
        transition: { duration: 0.18, ease: 'easeOut' },
      });
    }
  }, [isHovered, prefersReducedMotionHook, shimmerControls, textControls]);

  const handleHoverStart = () => {
    if (prefersReducedMotionHook) return;
    setIsHovered(true);
  };

  const handleHoverEnd = () => {
    if (prefersReducedMotionHook) return;
    setIsHovered(false);
  };

  return (
    <MotionLink
      to={to}
      className={`${CTA_DEMO_CLASSES} text-white hover:text-white focus-visible:text-white active:text-white`}
      style={{
        backgroundImage: `${shimmerBackground}, ${borderGlowGradient}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        backgroundSize: '100% 100%, 200% 200%',
        backgroundPosition: '0% 0%, 0% 50%',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'transparent',
        boxShadow: '0 14px 34px rgba(14, 48, 140, 0.35)',
        color: '#FFFFFF',
      }}
      whileHover={
        prefersReducedMotionHook
          ? {}
          : {
              scale: 1.04,
              y: -2,
              backgroundPosition: '0% 0%, 100% 45%',
              boxShadow: '0 26px 58px rgba(30, 88, 210, 0.5)',
              filter: 'drop-shadow(0px 18px 42px rgba(41, 87, 209, 0.35))',
              color: '#FFFFFF',
            }
      }
      whileTap={{ scale: 0.97, color: '#FFFFFF' }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      onHoverStart={handleHoverStart}
      onHoverEnd={handleHoverEnd}
      onFocus={handleHoverStart}
      onBlur={handleHoverEnd}
    >
      <motion.span
        className="relative z-20 text-white inline-flex items-center justify-center"
        style={{ color: '#FFFFFF', display: 'inline-flex', transformOrigin: '50% 50%' }}
        animate={prefersReducedMotionHook ? undefined : textControls}
      >
        {children}
      </motion.span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-70"
        style={{
          background:
            'radial-gradient(110% 110% at 50% 12%, rgba(135, 196, 255, 0.45) 0%, rgba(7, 25, 90, 0) 70%)',
          filter: 'blur(8px)',
          mixBlendMode: 'screen',
        }}
      />
      {!prefersReducedMotionHook && (
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute inset-[-35%] z-10 opacity-0 mix-blend-screen bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0.65)_45%,rgba(255,255,255,0.85)_52%,transparent_75%)]"
          initial={{ x: '-140%', opacity: 0 }}
          animate={shimmerControls}
        />
      )}
    </MotionLink>
  );
}

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}

function onParallaxScroll() {
  requestParallaxTick();
}

function onParallaxResize() {
  requestParallaxTick();
}

function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}

function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}

function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }

  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;

  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();

    // Skip only if far outside viewport to keep perf while ensuring near elements animate
    if (rect.bottom < farTop || rect.top > farBottom) return;

    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;

    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;

    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);

    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }

    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}

function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);

    ensureParallaxIO(rootMargin);
    startParallax();

    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };

    // Compute initial in-view based on an expanded viewport matching rootMargin
    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }

    parallaxItems.add(item);
    parallaxIo?.observe(el);

    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';

    // Ensure first-frame transform without waiting for IO or scroll
    updateParallax();

    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';

      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);

  return elRef;
}

export default function Home() {
  const [liveDomainsCount, setLiveDomainsCount] = useState(164); // fallback

  // Fetch live domains count
  useEffect(() => {
    const fetchLiveDomainsCount = async () => {
      try {
        const { count } = await supabase
          .from('domains')
          .select('*', { count: 'exact', head: true })
          .eq('domain_is_live', true)
          .eq('owned', true);

        if (count !== null) {
          setLiveDomainsCount(count);
        }
      } catch (error) {
        console.error('Error fetching live domains count:', error);
      }
    };

    fetchLiveDomainsCount();
  }, []);
  const orgData = orgJsonLd(
    defaultSite.baseUrl,
    defaultSite.name,
    `${defaultSite.baseUrl}/favicons/favicon-32.png`,
    [], // Add social media URLs here when available
  );

  // Subtle randomized, layered gradient background (computed once per mount)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = Math.random() * (220 - 205) + 205;
    const saturation = Math.random() * (78 - 65) + 65;
    const lightNearWhite = Math.random() * (98 - 96) + 96;
    const lightBlue = Math.random() * (92 - 88) + 88; // darken slightly for more visible blue
    const alphaSoft1 = Math.random() * (0.1 - 0.06) + 0.06; // slightly stronger overlay
    const alphaSoft2 = Math.random() * (0.08 - 0.04) + 0.04; // slightly stronger horizontal tint

    const centerX = Math.random() * (70 - 30) + 30;
    const centerY = Math.random() * (45 - 15) + 15;

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs (apply only to headings/subtitles and card grids; keep cards together)
  const heroTitleRef = useParallaxRef({ speed: -0.22, axis: 'y', maxTranslate: 108 });

  const statsGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });

  // Use reveal once hooks to prevent cards from disappearing after scroll
  const statsReveal = useRevealOnce({ amount: 0.3 });
  const purchaseReveal = useRevealOnce({ amount: 0.3 });
  const bundlesReveal = useRevealOnce({ amount: 0.25 });

  // Helper to merge parallax and reveal refs
  const mergeRefs = useCallback(
    (parallaxRef: React.RefObject<HTMLElement>, revealRef: React.RefObject<HTMLElement>) =>
      (node: HTMLElement | null) => {
        if (parallaxRef && 'current' in parallaxRef) {
          (parallaxRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
        if (revealRef && 'current' in revealRef) {
          (revealRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      },
    [],
  );

  const featuresTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 40 });
  const featuresSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const featuresGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });

  const purchaseTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 36 });
  const purchaseSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const purchaseGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 40 });

  const bundlesTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 32 });
  const bundlesSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const bundlesGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 36 });

  const ctaRef = useParallaxRef({ speed: -0.2, axis: 'y', maxTranslate: 90 });
  const ctaTitleRef = useParallaxRef({ speed: -0.22, axis: 'y', maxTranslate: 96 });
  const ctaSubRef = useParallaxRef({ speed: -0.28, axis: 'y', maxTranslate: 112 });
  const ctaButtonRef = useParallaxRef({ speed: -0.34, axis: 'y', maxTranslate: 128 });

  return (
    <>
      <Seo
        title={withTemplate('Premium domains for startups')}
        description="Secure checkout with industry-standard encryption. Browse brandable .com and next-gen TLDs."
        canonical={canonicalize('/')}
        og={{
          title: 'Premium domains for startups',
          description: 'Secure checkout with industry-standard encryption.',
          url: canonicalize('/'),
          type: 'website',
          image: `${defaultSite.baseUrl}/favicons/favicon-32.png`,
        }}
        twitter={{
          card: 'summary_large_image',
          title: 'Premium domains for startups',
          description: 'Secure checkout with industry-standard encryption.',
          image: `${defaultSite.baseUrl}/favicons/favicon-32.png`,
        }}
      />
      <JsonLD data={orgData} />

      <div className="flex flex-col min-h-screen" style={backgroundStyle}>
        {/* Hero Section */}
        <WavyBackground
          colors={['#F0F7FA', '#EBF2FC', '#F7F8FF', '#F7FCFF', '#EDF5FF']}
          speed="slow"
          waveOpacity={0.7}
          blur={0}
          backgroundFill="transparent"
          waveWidth={30}
          containerClassName="min-h-[85vh] flex items-center py-12 md:py-16"
        >
          <section className="relative w-full">
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-6xl mx-auto text-center stagger-fade-1">
                <h1 ref={heroTitleRef as any} className="text-6xl md:text-7xl font-bold mb-4 md:mb-6 liquid-gradient-text drop-shadow-lg">
                  <span className="block">
                    <Link to="/services" className="inline-block hover:opacity-90 transition-opacity">
                      <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                        Attain
                      </AnimatedGradientText>
                    </Link>{' '}
                    success with a premium{' '}
                    <Link to="/domains" className="inline-block hover:opacity-90 transition-opacity">
                      <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                        domain
                      </AnimatedGradientText>
                    </Link>{' '}
                    and expert help
                  </span>
                  <span className="mt-4 md:mt-6 block text-3xl md:text-4xl text-foreground/70 font-normal">
                    Get a successful strategy, brand, website, and business
                  </span>
                </h1>
              </div>
            </div>
          </section>
        </WavyBackground>

        {/* Lifecycle / Why Domains Fail Section */}
        <HomeLifecycle />

        {/* Paths and Service Pillars Section */}
        <HomePathsAndPillars />

        {/* Featured Domains Bento Section */}
        <HomeFeaturedDomainsBento />

        {/* Business Builder Configurator Section */}
        <HomeBusinessBuilder />

        {/* Services Snapshot Section */}
        <HomeServicesSnapshot />

        {/* Transformations & Testimonials Section */}
        <HomeTransformationsAndTestimonials />

        {/* Founder / Operator Strip */}
        <HomeFounderStrip />

        {/* Mini FAQ / Objection Snapshot */}
        <HomeMiniFaq />

        {/* Stats Section */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                ref={mergeRefs(statsGridRef as any, statsReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
                variants={statsContainerVariants}
                initial="hidden"
                animate={statsReveal.controls}
              >
                <motion.div variants={statsItemVariants}>
                  <Link
                    to="/domains"
                    className="apple-card p-8 text-center block hover:scale-105 transition-transform duration-200"
                  >
                    <div 
                      className="text-4xl font-bold mb-2"
                      style={{
                        background: 'linear-gradient(180deg, #0000FF 0%, #000000 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {liveDomainsCount}
                    </div>
                    <div className="text-muted-foreground">Premium Domains</div>
                  </Link>
                </motion.div>
                <motion.div variants={statsItemVariants}>
                  <Link
                    to="/FAQs"
                    className="apple-card p-8 text-center block hover:scale-105 transition-transform duration-200"
                  >
                    <div 
                      className="text-4xl font-bold mb-2"
                      style={{
                        background: 'linear-gradient(180deg, #0000FF 0%, #000000 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      24hrs
                    </div>
                    <div className="text-muted-foreground">Fast Transfers</div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16 stagger-fade-1">
                <h2 ref={featuresTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
                  Why Choose My Domains?
                </h2>
                <p ref={featuresSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
                  Premium domains acquired securely and quickly
                </p>
              </div>

              <div ref={featuresGridRef as any} className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="apple-card p-8 text-center group">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Star className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">Premium Quality</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Hand-curated domains that command attention and build trust with your audience.
                  </p>
                </div>

                <div className="apple-card p-8 text-center group">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">Verified & Secure</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    All domains are thoroughly verified with secure transfer processes and full
                    ownership rights.
                  </p>
                </div>

                <div className="apple-card p-8 text-center group">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Zap className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">Fast Transfers</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Quick and efficient domain transfers with dedicated support throughout the
                    process.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Purchase Ways - Simplified */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16 stagger-fade-1">
                <h2 ref={purchaseTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
                  Multiple Ways to Buy
                </h2>
                <p ref={purchaseSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
                  Choose how much you pay and when
                </p>
              </div>

              <motion.div
                ref={mergeRefs(purchaseGridRef as any, purchaseReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-3 gap-8"
                variants={gridContainerVariants}
                initial="hidden"
                animate={purchaseReveal.controls}
              >
                <motion.div variants={getGridItemVariants(0)} className="apple-card p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Zap className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">Buy It Now</h3>
                  <p className="text-muted-foreground mb-6">
                    Get immediate ownership with secure transfer. I handle all fees and paperwork.
                  </p>
                  <Button className="w-full apple-button">Purchase Now</Button>
                </motion.div>

                <motion.div variants={getGridItemVariants(1)} className="apple-card p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Star className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">Lease Monthly</h3>
                  <p className="text-muted-foreground mb-6">
                    Start using immediately with flexible monthly payments. Buy out anytime.
                  </p>
                  <Button className="w-full apple-button">Start Lease</Button>
                </motion.div>

                <motion.div variants={getGridItemVariants(2)} className="apple-card p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Award className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">Make an Offer</h3>
                  <p className="text-muted-foreground mb-6">
                    Submit a custom offer based on your budget and needs.
                  </p>
                  <Button className="w-full apple-button">Make Offer</Button>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bundles Preview */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16 stagger-fade-1">
                <h2 ref={bundlesTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
                  Explore by Category
                </h2>
                <p ref={bundlesSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
                  Quickly find the perfect domain
                </p>
              </div>

              <ExploreCategoriesStrip />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 apple-hero-bg relative overflow-hidden">
          <div ref={ctaRef as any} className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center stagger-fade-1">
              <h2
                ref={ctaTitleRef as any}
                className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg text-white"
              >
                Get Better Clients
              </h2>
              <p
                ref={ctaSubRef as any}
                className="text-3xl md:text-4xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 font-normal"
              >
                Find out how
              </p>

              <div ref={ctaButtonRef as any} className="flex justify-center pb-32">
                <SignatureCtaLink to="/services">Learn</SignatureCtaLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
